import { useState, useMemo, useEffect } from 'react';
import {Platform, View, Image, Text, FlatList } from 'react-native';
import React from 'react';
import styles from '@stylesheet';
import apis from '@api/api';
import SearchListUserItem from './components/SearchListUserItem';
import useGlobalUser from '@hooks/getGlobalUser';
import { DropDownPicker } from '@constants/Themed';
import UserListItem from '@navigation/screens/MidLevel/UsersScreen/Components/UserListItem';
import { useQueryClient } from '@tanstack/react-query';
import { UserFinder as UserFinderType } from './types/FinderTypes';
import { MaterialIcons } from '@expo/vector-icons';
import { Text as TamaguiText } from 'tamagui';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { getAgentAvatarSource } from '@utils/agentAvatar';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';


const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const looksLikeEmail = (value: unknown): value is string =>
  typeof value === 'string' && value.includes('@');

const toInviteePayload = (item: any) => {
  const value = item?.value ?? item?.id ?? item?._id ?? item?.email;
  const email = item?.email || (looksLikeEmail(value) ? value : '');
  return {
    ...item,
    editPermission: item?.editPermission ?? false,
    id: value,
    name: item?.name || '',
    surname: item?.surname || '',
    email,
  };
};

const resolveInviteesFromValues = (values: any[], itemPool: any[] = [], previousSelected: any[] = []) => {
  return values.map((val: any) => {
    const found = itemPool.find((item: any) => item.value === val)
      || previousSelected.find((user: any) =>
        user.value === val || user.id === val || user._id === val || user.email === val
      );
    if (found) {
      return toInviteePayload(found);
    }
    if (looksLikeEmail(val)) {
      return toInviteePayload({
        value: val,
        id: val,
        label: val,
        name: val,
        email: val,
      });
    }
    return null;
  }).filter(Boolean);
};

const normalizeAvatar = (avatar: any, context?: any) => {
  if (!avatar && !context) return '';
  let avatarValue = '';
  if (typeof avatar === 'object') {
    avatarValue = avatar.img || '';
  } else {
    avatarValue = avatar;
  }

  const normalizedAvatar = typeof avatarValue === 'string' ? avatarValue.trim() : '';
  const avatarFileName = normalizedAvatar
    .split('?')[0]
    .split('#')[0]
    .split('/')
    .pop() || normalizedAvatar;
  
  const isKnownBundledAgentAvatar = ['Assistant.png', 'BusinessDeveloper.png', 'SalesMarketer.png', 'Researcher.png']
    .includes(avatarFileName);
  const normalizedAvatarFileNameLower = avatarFileName.toLowerCase();
  const isOpenClawAvatar = normalizedAvatarFileNameLower === 'openclaw' || normalizedAvatarFileNameLower === 'openclaw.png' || context?._id === 'openclaw' || context?.id === 'openclaw';

  if (isKnownBundledAgentAvatar || isOpenClawAvatar) {
    return getAgentAvatarSource({
      avatar: avatarFileName,
      type: isOpenClawAvatar ? 'openclaw' : undefined,
      _id: isOpenClawAvatar ? 'openclaw' : context?._id || context?.id,
      isOpenClaw: isOpenClawAvatar,
    });
  }

  if (normalizedAvatar.length > 0 && !normalizedAvatar.startsWith('http') && !normalizedAvatar.startsWith('data:')) {
    return `https://storage.googleapis.com/smartsaas-avatar-store/${normalizedAvatar}`;
  }

  return normalizedAvatar || avatarValue;
};

const UserFinder = ({ hideList, team, agentSelect, anyUser, participant, onOpen, onClose, forceSingle, setCallback, disabled, selected, removePermission, forceRow = false, theme = true, hasError = false, maxDropdownHeight = 350, includeSelf }: UserFinderType) => {
  const [value, setValue] = useState<any>(() => {
    if (forceSingle) {
      if (selected && typeof selected === 'object' && 'value' in selected) return selected.value;
      if (selected && typeof selected === 'object' && '_id' in selected) return selected._id;
      return (selected !== undefined) ? selected : null;
    } else {
      if (Array.isArray(selected)) {
        return selected.map((item: any) => {
          if (item && typeof item === 'object') {
            return item.value || item._id || item.id;
          }
          return item;
        });
      }
      return (selected ? [selected] : []);
    }
  });

  // For multi-select, track selected values separately from the library's value state
  const [multiSelectedValues, setMultiSelectedValues] = useState<any[]>(() => {
    if (Array.isArray(selected)) {
      return selected.map((item: any) => {
        if (item && typeof item === 'object') {
          return item.value || item._id || item.id;
        }
        return item;
      });
    }
    return selected ? [selected] : [];
  });
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const userId = useGlobalUser();
  const userData = useSelector((state: DefaultRootState) => state.promiseData.data?.userData);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [height, setHeight] = useState(50);
  const [emailError, setEmailError] = useState<string | null>(null);

  const selfItem = useMemo(() => {
    if (!userData || !includeSelf) return null;
    const name = userData.profile_information?.name || userData.name || '';
    const surname = userData.profile_information?.surname || userData.surname || '';
    const email = userData.email || '';
    const avatar = normalizeAvatar(userData.profile_information?.avatar || userData.avatar || userData.contactProfilePicture || userData.contactInfo?.profilePicture, userData);
    const fullName = `${name} ${surname}`.trim() || email || 'You';
    const label = email ? `${fullName} (You)\n${email}` : `${fullName} (You)`;
    
    return {
      label,
      value: userId,
      name,
      surname,
      email,
      avatar,
      _id: userId,
      isSelf: true
    };
  }, [userData, userId, includeSelf]);

  // Sync value state with selected prop when it changes externally
  useEffect(() => {
    let newValue: any;
    if (forceSingle) {
      if (selected && typeof selected === 'object' && 'value' in selected) newValue = selected.value;
      else if (selected && typeof selected === 'object' && '_id' in selected) newValue = selected._id;
      else newValue = selected;
    } else {
      if (Array.isArray(selected)) {
        newValue = selected.map((item: any) => {
          if (item && typeof item === 'object') {
            return item.value || item._id || item.id;
          }
          return item;
        });
      } else {
        newValue = selected ? [selected] : [];
      }
    }

    if (JSON.stringify(newValue) !== JSON.stringify(value)) {
      setValue(newValue);
    }
  }, [selected, forceSingle]);

  const queryClient = useQueryClient();
  const companyListData = (team || agentSelect || participant) ? queryClient.getQueryData(['companyList']) : null;
  const companyMembersData = (team || agentSelect || participant) ? queryClient.getQueryData(['companyMembers']) : null;
  // Fallback to userHierarchy query which should have populated users
  const userHierarchyData = (team || agentSelect || participant) ? queryClient.getQueryData(['userHierarchy']) : null;

  // Transform team members to DropDownPicker format (label, value, etc.)
  const teamMembers = useMemo(() => {
    if (!team && !participant) {
      return [];
    }

    let rawUsers: any[] = [];

    // Handle companyMembers (user hierarchy data) - could be array or wrapped in data property
    if (companyMembersData) {
      if (Array.isArray(companyMembersData)) {
        rawUsers = companyMembersData;
      } else if (typeof companyMembersData === 'object' && companyMembersData !== null && 'data' in companyMembersData) {
        const data = (companyMembersData as any).data;
        if (Array.isArray(data)) {
          rawUsers = data;
        }
      }
    }

    // Fallback: Try userHierarchy query which should have populated users
    if (rawUsers.length === 0 && userHierarchyData) {
      if (typeof userHierarchyData === 'object' && userHierarchyData !== null && 'data' in userHierarchyData) {
        const data = (userHierarchyData as any).data;
        if (Array.isArray(data)) {
          rawUsers = data;
        }
      } else if (Array.isArray(userHierarchyData)) {
        rawUsers = userHierarchyData;
      }
    }
    // Handle companyList (companies with users arrays) - extract users from companies
    if (rawUsers.length === 0 && companyListData) {
      let companies: any[] = [];
      if (Array.isArray(companyListData)) {
        companies = companyListData;
      } else if (typeof companyListData === 'object' && companyListData !== null && 'data' in companyListData) {
        const data = (companyListData as any).data;
        if (Array.isArray(data)) {
          companies = data;
        }
      }

      // Extract users from all companies' users arrays
      companies.forEach((company: any) => {
        if (company.users && Array.isArray(company.users)) {
          rawUsers.push(...company.users);
        }
      });
    }

    if (!rawUsers || rawUsers.length === 0) {
      return [];
    }

    const transformed = rawUsers
      .filter((member: any) => {
        // Check if member.user is a populated object (not a string ID)
        // Backend should populate users, but handle both cases
        if (member.user && typeof member.user === 'object' && member.user._id) {
          // Populated user object - valid
          return true;
        }

        // If member.user is a string ID, we can't use it without fetching user data
        // Skip these entries as we don't have the user details
        if (member.user && typeof member.user === 'string') {
          console.log('UserFinder: Skipping member with unpopulated user ID', {
            userId: member.user,
            memberId: member._id
          });
          return false;
        }

        // If member itself is a user object (direct user, not nested)
        if (member._id && (member.email || member.profile_information)) {
          return true;
        }

        // Invalid entry
        console.log('UserFinder: Filtered out invalid member', {
          hasUser: !!member.user,
          userType: typeof member.user,
          userIsObject: member.user && typeof member.user === 'object',
          userHasId: member.user?._id,
          memberKeys: Object.keys(member || {})
        });
        return false;
      })
      .map((member: any) => {
        // Handle populated nested structure (member.user is an object)
        if (member.user && typeof member.user === 'object' && member.user._id) {
          const user = member.user;
          const userId = user._id || user.id;
          const name = user.profile_information?.name || user.name || '';
          const surname = user.profile_information?.surname || user.surname || '';
          const email = user.email || '';
          const avatar = normalizeAvatar(user.profile_information?.avatar || user.avatar || user.contactProfilePicture || user.contactInfo?.profilePicture, user);

          // Format label like searchUsers: "Name Surname\nemail"
          const fullName = `${name} ${surname}`.trim() || email || 'Unknown User';
          const label = email ? `${fullName}\n${email}` : fullName;

          return {
            label,
            value: userId,
            name,
            surname,
            email,
            avatar,
            _id: userId,
            // Preserve original member data for reference
            originalMember: member
          };
        }

        // Handle direct user object (member itself is the user)
        if (member._id && (member.email || member.profile_information)) {
          const userId = member._id || member.id;
          const name = member.profile_information?.name || member.name || '';
          const surname = member.profile_information?.surname || member.surname || '';
          const email = member.email || '';
          const avatar = normalizeAvatar(member.profile_information?.avatar || member.avatar || member.contactProfilePicture || member.contactInfo?.profilePicture, member);

          // Format label like searchUsers: "Name Surname\nemail"
          const fullName = `${name} ${surname}`.trim() || email || 'Unknown User';
          const label = email ? `${fullName}\n${email}` : fullName;

          return {
            label,
            value: userId,
            name,
            surname,
            email,
            avatar,
            _id: userId
          };
        }

        // Fallback (shouldn't reach here due to filter)
        console.warn('UserFinder: Unexpected member structure in map', member);
        return null;
      })
      .filter((item: any) => {
        // Remove null entries and current user
        if (!item) return false;
        const isNotCurrentUser = includeSelf || item.value !== userId;
        if (!isNotCurrentUser) {
          console.log('UserFinder: Filtered out current user', item);
        }
        return isNotCurrentUser;
      });

    if (includeSelf && selfItem && !transformed.find((i: any) => i.value === userId)) {
      transformed.unshift(selfItem);
    }

    return transformed;
  }, [team, companyListData, companyMembersData, userHierarchyData, userId]);

  const agentMembers = useMemo(() => {
    if (!agentSelect) return [];

    // Agents are embedded in the same companyMembers/userHierarchy/companyList data
    // as regular users, identified by isAgent:true or role:'agent'
    let rawMembers: any[] = [];

    if (companyMembersData) {
      if (Array.isArray(companyMembersData)) {
        rawMembers = companyMembersData;
      } else if (typeof companyMembersData === 'object' && 'data' in companyMembersData) {
        const data = (companyMembersData as any).data;
        if (Array.isArray(data)) rawMembers = data;
      }
    }

    if (rawMembers.length === 0 && userHierarchyData) {
      if (Array.isArray(userHierarchyData)) {
        rawMembers = userHierarchyData;
      } else if (typeof userHierarchyData === 'object' && 'data' in userHierarchyData) {
        const data = (userHierarchyData as any).data;
        if (Array.isArray(data)) rawMembers = data;
      }
    }

    if (rawMembers.length === 0 && companyListData) {
      let companies: any[] = [];
      if (Array.isArray(companyListData)) {
        companies = companyListData;
      } else if (typeof companyListData === 'object' && 'data' in companyListData) {
        const data = (companyListData as any).data;
        if (Array.isArray(data)) companies = data;
      }
      companies.forEach((company: any) => {
        if (company.users && Array.isArray(company.users)) {
          rawMembers.push(...company.users);
        }
      });
    }

    return rawMembers
      .filter((member: any) => member.isAgent === true || member.role === 'agent' || member.type === 'agent')
      .map((member: any) => {
        const agentId = member._id || member.user?._id;
        const name = member.user?.profile_information?.name || member.agentType || 'Agent';
        const agentType: string = member.agentType || member.type || '';
        const typeLabel = agentType
          ? agentType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
          : '';
        const avatarImg = member.user?.profile_information?.avatar?.img
          || member.user?.profile_information?.avatar
          || member.avatar
          || '';
        const avatar = normalizeAvatar(avatarImg, { _id: agentId, avatar: avatarImg });
        const label = typeLabel ? `${name}\n${typeLabel}` : name;

        return {
          label,
          value: agentId,
          name,
          surname: typeLabel,
          email: typeLabel,
          avatar,
          _id: agentId,
          isAgent: true,
        };
      });
  }, [agentSelect, companyMembersData, userHierarchyData, companyListData]);

  // Get the currently selected user for display
  const currentSelectedUser = useMemo(() => {
    if (!selectedUsers || selectedUsers.length === 0) return null;
    return forceSingle ? selectedUsers[0] : selectedUsers[0]; // For display, show first selected
  }, [selectedUsers, forceSingle]);

  // Sync selectedUsers with value from DropDownPicker
  // This ensures selectedUsers stays in sync when value changes (e.g., from external updates or initial load)
  useEffect(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      setSelectedUsers([]);
      return;
    }

    const allItems = agentSelect ? agentMembers : ((team || participant) ? teamMembers : items);
    if (!allItems || allItems.length === 0) {
      // If items aren't loaded yet, wait for them to load
      return;
    }

    let usersToSet: any[] = [];

    if (forceSingle) {
      // Single selection mode
      const selectedItem = allItems.find((item: any) => item.value === value);
      if (selectedItem) {
        usersToSet = [{
          ...selectedItem,
          editPermission: false,
          id: selectedItem.value || selectedItem._id
        }];
      }
    } else {
      // Multiple selection mode
      if (Array.isArray(value)) {
        const valueArray = value as any[];
        const selectedItems = allItems.filter((item: any) => valueArray.includes(item.value));
        usersToSet = selectedItems.map((item: any) => ({
          ...item,
          editPermission: false,
          id: item.value || item._id
        }));
      } else if (value) {
        // Handle case where value is a single value but not in array format
        const selectedItem = allItems.find((item: any) => item.value === value);
        if (selectedItem) {
          usersToSet = [{
            ...selectedItem,
            editPermission: false,
            id: selectedItem.value || selectedItem._id
          }];
        }
      }
    }

    setSelectedUsers(usersToSet);
  }, [value, team, teamMembers, items, forceSingle]);

  const handleItemSelect = (selectedItem: any) => {
    let hasInvalidEmail = false;
    let hasCustomAgentError = false;

    if (forceSingle) {
      const isCustom = !selectedItem._id && !selectedItem.originalMember && !selectedItem.isAgent && !selectedItem.avatar;
      
      if (isCustom && agentSelect) {
        setEmailError('Must select from deployed agents');
        setTimeout(() => setValue(value), 0);
        return;
      }
      
      if (isCustom && !validateEmail(selectedItem.value)) {
        setEmailError('Must be a valid e-mail address');
        setTimeout(() => setValue(value), 0);
        return;
      }
      setEmailError(null);

      const transformedItem = toInviteePayload({
        ...selectedItem,
        email: selectedItem.email || (isCustom ? selectedItem.value : ''),
      });
      setSelectedUsers([transformedItem]);
      setValue(selectedItem.value);
      setCallback?.(transformedItem);
    } else {
      const itemsArray = Array.isArray(selectedItem) ? selectedItem : [selectedItem];
      
      const validItems = itemsArray.filter((item: any) => {
        // Items with label and value from search are valid users
        // Only treat as custom if it has NO identifying properties at all
        const isCustom = !item._id && !item.originalMember && !item.isAgent && !item.avatar && !item.name && !item.email && !item.surname;
        
        if (isCustom && agentSelect) {
          hasCustomAgentError = true;
          return false;
        }
        
        // Only validate as email if it's truly a custom entry (no avatar, no user data)
        if (isCustom && !validateEmail(item.value)) {
          hasInvalidEmail = true;
          return false;
        }
        return true;
      }); 

      if (hasCustomAgentError) {
        setEmailError('Must select from deployed agents');
      } else if (hasInvalidEmail) {
        setEmailError('Must be a valid e-mail address');
      } else {
        setEmailError(null);
      }

      const transformedItems = validItems.map((item: any) => {
        const isCustom = !item._id && !item.originalMember && !item.isAgent && !item.avatar;
        return toInviteePayload({
          ...item,
          email: item.email || (isCustom ? item.value : ''),
        });
      });
      setSelectedUsers(transformedItems);
      const selectedValues = validItems.map((item: any) => item.value);
      // Ensure value is always an array in multi-select mode
      setValue(Array.isArray(selectedValues) ? selectedValues : [selectedValues]);
      setCallback?.(transformedItems);
    }
  };

  return (
    <View style={{ flex: 1, width: '100%', backgroundColor: 'transparent' }}>
      {/* Heading Section */}
      {!hideList && (
      <View style={{ marginBottom: 20 }}>
        <TamaguiText style={{
          fontSize: 20,
          fontWeight: '700',
          color: theme ? '#1e293b' : '#f8fafc',
          marginBottom: 16
        }}>
          Select User for Context Analysis
        </TamaguiText>

        {/* User Label */}
        <TamaguiText style={{
          fontSize: 14,
          fontWeight: '600',
          color: theme ? '#475569' : '#94a3b8',
          marginBottom: 8
        }}>
          User
        </TamaguiText>
      </View>
      )}

      {/* Dropdown Section */}
      <View style={{
        zIndex: 999999,
        minHeight: 50,
        borderRadius: 12,
        backgroundColor: 'transparent',
        position: 'relative',
        marginBottom: 20
      }}>
        <DropDownPicker
          zIndex={999999}
          zIndexInverse={999998}
          setOpen={() => {
            setOpen(true);
            setHeight(280);
            onOpen && onOpen?.();
          }}
          open={open}
          key={selected}
          multiple={false}
          loading={loading}
          onOpen={async () => {
            setLoading(true);
            onOpen && onOpen?.();
            // Fetch users if anyUser is true OR if not using team/agentSelect/participant mode
            if (anyUser || (!team && !agentSelect && !participant)) {
              await apis.searchUsers("")
                .then((items: any) => {
                  const filteredItems = items
                    .filter(item => includeSelf || item.value !== userId)
                    .map(item => ({
                      ...item,
                      avatar: normalizeAvatar(item.avatar)
                    }));
                  
                  if (includeSelf && selfItem && !filteredItems.find(i => i.value === userId)) {
                    filteredItems.unshift(selfItem);
                  }
                  setItems(filteredItems)
                })
                .catch(err => { console.error(err) })
                .finally(() => {
                  setLoading(false)
                })
            } else {
              // In team/agentSelect/participant mode, data comes from cached query (already transformed)
              setLoading(false);
            }
          }}
          style={[
            styles.dropdownInput,
            {
              paddingHorizontal: 16,
              paddingVertical: 14,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: (hasError || emailError) ? '#ef4444' : (open ? '#3b82f6' : theme ? '#e2e8f0' : '#374151'),
              backgroundColor: theme ? 'white' : '#1e293b',
              minHeight: 50,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 999999,
              zIndex: 999999,
              position: 'relative'
            }
          ]}
          textStyle={{
            color: (!forceSingle && multiSelectedValues.length > 0)
              ? (theme ? '#1e293b' : '#f8fafc')
              : (value && (Array.isArray(value) ? value.length > 0 : value)
                ? (theme ? '#1e293b' : '#f8fafc')
                : (theme ? '#64748b' : '#94a3b8')),
            fontSize: 16,
            fontWeight: (!forceSingle && multiSelectedValues.length > 0)
              ? '600'
              : (value && (Array.isArray(value) ? value.length > 0 : value) ? '600' : '400')
          }}
          placeholderStyle={{
            color: theme ? '#64748b' : '#94a3b8',
            fontSize: 16
          }}
          mode={'SIMPLE'}
          items={agentSelect ? agentMembers : ((team || participant) ? teamMembers : items)}
          onClose={() => {
            setHeight(50);
            setOpen(false);
            onClose && onClose?.()
          }}
          onChangeSearchText={async (text) => {
            setLoading(true);
            await apis.searchUsers(text)
              .then((items: any) => {
                const normalizedItems = items
                  .filter(item => includeSelf || item.value !== userId)
                  .map(item => ({
                    ...item,
                    avatar: normalizeAvatar(item.avatar)
                  }));
                
                if (includeSelf && selfItem && !normalizedItems.find(i => i.value === userId)) {
                  const searchLower = text.toLowerCase();
                  if (selfItem.label.toLowerCase().includes(searchLower) || selfItem.email.toLowerCase().includes(searchLower)) {
                    normalizedItems.unshift(selfItem);
                  }
                }
                setItems(normalizedItems)
              })
              .catch(err => { console.error(err) })
              .finally(() => {
                setLoading(false)
              })
          }}
          setItems={setItems}
          debounce
          disabled={disabled || (agentSelect && agentMembers.length === 0)}
          value={forceSingle ? value : (multiSelectedValues.length > 0 ? multiSelectedValues[multiSelectedValues.length - 1] : null)}
          setValue={forceSingle ? setValue : (() => { })}
          showTickIcon
          badgeDotColors={["#e76f51", "#00b4d8", "#e9c46a", "#e76f51", "#8ac926", "#00b4d8", "#e9c46a"]}
          selectedItemContainerStyle={{
            backgroundColor: theme ? '#eff6ff' : '#1e3a8a',
            borderRadius: 8,
            marginVertical: 2
          }}
          listItemContainerStyle={{
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#f1f5f9' : '#334155'
          }}
          listItemLabelStyle={{
            fontSize: 12,
            color: theme ? '#1e293b' : '#f8fafc',
            fontWeight: '500'
          }}
          closeAfterSelecting={forceSingle ? true : false}
          dropDownContainerStyle={{
            top: '100%',
            marginTop: -15,
            borderRadius: 12,
            borderColor: theme ? '#e2e8f0' : '#374151',
            borderWidth: 1,
            zIndex: 999999,
            elevation: 999999,
            backgroundColor: theme ? 'white' : '#1e293b',
            position: 'absolute',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.15,
            shadowRadius: 12,
            maxHeight: maxDropdownHeight
          }}
          searchContainerStyle={{
            padding: 12,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#f1f5f9' : '#334155'
          }}
          searchTextInputStyle={{
            backgroundColor: theme ? '#f8fafc' : '#0f172a',
            borderWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#475569',
            borderRadius: 8,
            paddingHorizontal: 12,
            paddingVertical: 8,
            fontSize: 14,
            color: theme ? '#1e293b' : '#f8fafc'
          }}
          placeholder={agentSelect && agentMembers.length === 0 ? "No agents deployed" : (agentSelect ? 'Select an agent...' : (participant ? 'Select a participant...' : (team ? 'Select from your team' : (anyUser ? 'Search any user...' : 'Search users by email or name...'))))}
          searchable={true}
          modal={false}
          renderListItem={(props) => (
            <SearchListUserItem
              {...props}
              isMultiSelect={!forceSingle}
              selectedValues={forceSingle ? value : multiSelectedValues}
            />
          )}
          addCustomItem={true}
          onSelectItem={(item: any) => {
            // For multi-select mode, manually toggle items
            if (!forceSingle && item && !Array.isArray(item)) {
              const itemValue = typeof item === 'object' ? item.value : item;
              const alreadySelected = multiSelectedValues.includes(itemValue);

              const allItems = agentSelect ? agentMembers : ((team || participant) ? teamMembers : items);
              if (alreadySelected) {
                const updatedValues = multiSelectedValues.filter((v: any) => v !== itemValue);
                setMultiSelectedValues(updatedValues);
                const updatedUsers = resolveInviteesFromValues(updatedValues, allItems, selectedUsers);
                setSelectedUsers(updatedUsers);
                setCallback?.(updatedUsers);
              } else {
                const updatedValues = [...multiSelectedValues, itemValue];
                setMultiSelectedValues(updatedValues);
                const newItem = typeof item === 'object' ? item : { value: item, label: item };
                const updatedUsers = resolveInviteesFromValues(
                  updatedValues,
                  [...(allItems || []), toInviteePayload(newItem)],
                  selectedUsers
                );
                setSelectedUsers(updatedUsers);
                setCallback?.(updatedUsers);
              }

              // Prevent the library from updating its internal value
              return false;
            } else {
              handleItemSelect(item);
            }
          }}
          onChange={(e: any) => {
            // For multi-select mode, ignore the library's value changes
            // We manage selections manually via onSelectItem
            if (forceSingle) {
              // Only sync for single-select mode
              if (e.value) {
                const allItems = agentSelect ? agentMembers : ((team || participant) ? teamMembers : items);
                if (allItems && allItems.length > 0) {
                  const selectedItem = allItems.find((item: any) => item.value === e.value);
                  if (selectedItem) {
                    const transformed = {
                      ...selectedItem,
                      editPermission: false,
                      id: selectedItem.value || selectedItem._id
                    };
                    setSelectedUsers([transformed]);
                    setCallback?.(transformed);
                  }
                }
              }
            }
          }}
          searchPlaceholder={anyUser ? 'Enter name or email...' : 'Enter an email address'}
        />
        {emailError && (
          <TamaguiText style={{ color: '#ef4444', fontSize: 13, marginTop: 6, marginLeft: 4 }}>
            {emailError}
          </TamaguiText>
        )}
      </View>

      {/* User Display Area */}
      {!hideList && (
      <View style={{
        backgroundColor: theme ? 'white' : '#1e293b',
        borderRadius: 16,
        padding: 24,
        minHeight: 200,
        borderWidth: 2,
        borderColor: theme ? '#e2e8f0' : '#334155',
        borderStyle: 'dashed',
        alignItems: 'center',
        justifyContent: 'center',
        ...(Platform.OS === 'web' && {
          backdropFilter: 'blur(10px)',
        })
      }}>
        {!currentSelectedUser ? (
          // Empty state
          <View style={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            paddingVertical: 40
          }}>
            <MaterialIcons
              name="person-outline"
              size={64}
              color={theme ? '#cbd5e1' : '#475569'}
              style={{ marginBottom: 16 }}
            />
            <TamaguiText style={{
              color: theme ? '#64748b' : '#94a3b8',
              fontSize: 16,
              fontWeight: '600',
              marginBottom: 8,
              textAlign: 'center'
            }}>
              No User Selected
            </TamaguiText>
            <TamaguiText style={{
              color: theme ? '#94a3b8' : '#64748b',
              fontSize: 14,
              textAlign: 'center'
            }}>
              Select a user from the dropdown above
            </TamaguiText>
          </View>
        ) : (
          // Selected user display
          <View style={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            paddingVertical: 20
          }}>
            {/* Avatar */}
            <View style={{
              marginBottom: 16,
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AvatarHandler
                avatar={currentSelectedUser.avatar || ''}
                avatarImageStyle={{
                  width: 80,
                  height: 80,
                  borderRadius: 40,
                  borderWidth: 3,
                  borderColor: theme ? '#e2e8f0' : '#334155'
                }}
                replacementImageStyle={{
                  width: 80,
                  height: 80,
                  borderRadius: 40
                }}
              />
            </View>

            {/* Name */}
            <TamaguiText style={{
              color: theme ? '#1e293b' : '#f8fafc',
              fontSize: 20,
              fontWeight: '700',
              marginBottom: 8,
              textAlign: 'center'
            }}>
              {`${currentSelectedUser.name || ''} ${currentSelectedUser.surname || ''}`.trim() || 'Unknown User'}
            </TamaguiText>

            {/* Email */}
            <TamaguiText style={{
              color: theme ? '#64748b' : '#94a3b8',
              fontSize: 16,
              fontWeight: '400',
              textAlign: 'center',
              flexWrap: 'wrap',
              maxWidth: '90%'
            }}>
              {currentSelectedUser.email || ''}
            </TamaguiText>
          </View>
        )}

        {/* Multiple users list (if not forceSingle and multiple selected) */}
        {!forceSingle && selectedUsers.length > 1 && (
          <View style={{
            width: '100%',
            marginTop: 24,
            paddingTop: 24,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e2e8f0' : '#334155'
          }}>
            <TamaguiText style={{
              color: theme ? '#475569' : '#94a3b8',
              fontSize: 14,
              fontWeight: '600',
              marginBottom: 12
            }}>
              Additional Selected Users ({selectedUsers.length - 1})
            </TamaguiText>
            <FlatList
              style={{
                maxHeight: 200
              }}
              showsVerticalScrollIndicator={true}
              data={selectedUsers.slice(1)}
              horizontal={forceRow}
              contentContainerStyle={{
                gap: 8,
                paddingVertical: 10
              }}
              renderItem={({ item, index }) => (
                <UserListItem
                  removePermission={removePermission}
                  row={forceRow}
                  item={item}
                  index={index + 1}
                  filterUser={(id: string) => {
                    const updatedUsers = selectedUsers.filter((user) => user.id !== id);
                    setSelectedUsers(updatedUsers);
                    if (forceSingle) {
                      setValue(null);
                    } else {
                      const updatedValues = updatedUsers.map((user) => user.id || user.value || user._id);
                      setValue(updatedValues);
                    }
                    setCallback?.(updatedUsers);
                  }}
                />
              )}
            />
          </View>
        )}
      </View>
      )}
    </View>
  )
}
export default UserFinder;
